import { useState, useCallback } from 'react';
import { safeStorage } from '@/lib/safeStorage';
import { supabase } from '../lib/supabase';

export const FREE_MONTHLY_INTEREST_SEARCHES = 2;
const FIRST_SAVE_BONUS_INTEREST = 5;
const FIRST_SAVE_BONUS_DEEP = 3;
const TIP_REWARD_INTEREST = 10;
const TIP_REWARD_DEEP = 5;
const REFERRAL_REWARD_INTEREST = 2;
const REFERRAL_REWARD_DEEP = 2;

// Unregistered users get 3 interest searches tracked in localStorage
const UNREGISTERED_MAX = 3;
const LS_KEY = 'tb_interest_count';
const LS_REFERRER_KEY = 'tb_ref';

/** Store a referrer user ID from a ?ref= URL param (call on app load) */
export function captureReferrer(): void {
  if (typeof window === 'undefined') return;
  try {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get('ref');
    if (ref && !safeStorage.getItem(LS_REFERRER_KEY)) {
      safeStorage.setItem(LS_REFERRER_KEY, ref);
    }
  } catch {
    // localStorage may be unavailable in private browsing on some devices
  }
}

/** Get stored referrer ID (if any) */
export function getStoredReferrer(): string | null {
  try {
    return safeStorage.getItem(LS_REFERRER_KEY);
  } catch {
    return null;
  }
}

/** Clear stored referrer after processing */
export function clearStoredReferrer(): void {
  try {
    safeStorage.remove(LS_REFERRER_KEY);
  } catch {
    // ignore
  }
}

export interface SearchCredits {
  interestSearchesUsed: number;
  interestSearchesBonus: number;
  interestSearchesRemaining: number;
  deepSearchesRemaining: number;
  firstSaveBonusClaimed: boolean;
  tipRewardClaimed: boolean;
}

function getCurrentMonth(): string {
  // Use UTC to avoid timezone-dependent monthly resets
  const now = new Date();
  return `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}`;
}

export function getUnregisteredInterestCount(): number {
  try {
    const val = safeStorage.getItem(LS_KEY);
    return val ? parseInt(val, 10) : 0;
  } catch {
    return 0;
  }
}

export function canUnregisteredUseInterest(): boolean {
  return getUnregisteredInterestCount() < UNREGISTERED_MAX;
}

export function incrementUnregisteredInterest(): void {
  try {
    const current = getUnregisteredInterestCount();
    safeStorage.setItem(LS_KEY, String(current + 1));
  } catch {
    // ignore
  }
}

export function decrementUnregisteredInterest(): void {
  try {
    const current = getUnregisteredInterestCount();
    safeStorage.setItem(LS_KEY, String(Math.max(0, current - 1)));
  } catch {
    // ignore
  }
}

export function useSearchCredits() {
  const [credits, setCredits] = useState<SearchCredits | null>(null);
  const [loading, setLoading] = useState(false);

  /** Ensure a user_settings row exists for the given user, creating one if needed. */
  const ensureUserRow = useCallback(async (userId: string): Promise<void> => {
    if (!supabase) return;
    // Credit columns take their defaults; users can only write preferences (#67).
    await supabase
      .from('user_settings')
      .upsert({ user_id: userId }, { onConflict: 'user_id', ignoreDuplicates: true });
  }, []);

  const loadCredits = useCallback(async (userId: string): Promise<SearchCredits | null> => {
    if (!supabase) return null;
    setLoading(true);

    try {
      const { data } = await supabase
        .from('user_settings')
        .select('interest_searches_used, interest_searches_reset_month, interest_searches_bonus, deep_searches_remaining, first_save_bonus_claimed, tip_reward_claimed')
        .eq('user_id', userId)
        .maybeSingle();

      if (!data) {
        // New user — create the row so future update() calls work
        await ensureUserRow(userId);
        const fresh: SearchCredits = {
          interestSearchesUsed: 0,
          interestSearchesBonus: 0,
          interestSearchesRemaining: FREE_MONTHLY_INTEREST_SEARCHES,
          deepSearchesRemaining: 0,
          firstSaveBonusClaimed: false,
          tipRewardClaimed: false,
        };
        setCredits(fresh);
        setLoading(false);
        return fresh;
      }

      const currentMonth = getCurrentMonth();
      let used = data.interest_searches_used || 0;

      // Monthly reset: a new month starts from zero. use_interest_search
      // applies the same rule server-side when the next search is spent.
      if (data.interest_searches_reset_month !== currentMonth) {
        used = 0;
      }

      const bonus = Math.max(0, data.interest_searches_bonus || 0);
      const remaining = Math.max(0, (FREE_MONTHLY_INTEREST_SEARCHES + bonus) - used);

      const result: SearchCredits = {
        interestSearchesUsed: used,
        interestSearchesBonus: bonus,
        interestSearchesRemaining: remaining,
        deepSearchesRemaining: Math.max(0, data.deep_searches_remaining || 0),
        firstSaveBonusClaimed: data.first_save_bonus_claimed || false,
        tipRewardClaimed: data.tip_reward_claimed || false,
      };

      setCredits(result);
      setLoading(false);
      return result;
    } catch {
      setLoading(false);
      return null;
    }
  }, [ensureUserRow]);

  /**
   * Atomically consume one interest search credit via RPC.
   * Single round-trip, row-level lock prevents races.
   */
  const consumeInterestSearch = useCallback(async (userId: string): Promise<boolean> => {
    if (!supabase) return false;

    const { data, error } = await supabase.rpc('use_interest_search', {
      p_user_id: userId,
      p_current_month: getCurrentMonth(),
      p_free_monthly: FREE_MONTHLY_INTEREST_SEARCHES,
    });

    if (error || !data?.[0]?.success) return false;

    const row = data[0];
    setCredits(prev => prev ? {
      ...prev,
      interestSearchesUsed: row.new_used,
      interestSearchesRemaining: row.new_remaining,
    } : null);

    return true;
  }, []);

  /**
   * Refund one interest search credit via atomic RPC.
   */
  const refundInterestSearch = useCallback(async (userId: string): Promise<boolean> => {
    if (!supabase) return false;

    const { error } = await supabase.rpc('refund_interest_search', { p_user_id: userId });
    if (error) return false;

    // Reload to get accurate counts after atomic decrement
    await loadCredits(userId);
    return true;
  }, [loadCredits]);

  /**
   * Atomically consume one deep search credit via RPC.
   */
  const consumeDeepSearch = useCallback(async (userId: string): Promise<boolean> => {
    if (!supabase) return false;

    const { data, error } = await supabase.rpc('use_deep_search', { p_user_id: userId });

    if (error || !data?.[0]?.success) return false;

    setCredits(prev => prev ? {
      ...prev,
      deepSearchesRemaining: data[0].new_remaining,
    } : null);

    return true;
  }, []);

  const claimFirstSaveBonus = useCallback(async (userId: string): Promise<boolean> => {
    if (!supabase) return false;

    const { data, error } = await supabase.rpc('claim_first_save_bonus', {
      p_user_id: userId,
      p_interest_bonus: FIRST_SAVE_BONUS_INTEREST,
      p_deep_bonus: FIRST_SAVE_BONUS_DEEP,
    });

    if (error || !data) return false;

    await loadCredits(userId);
    return true;
  }, [loadCredits]);

  /** Secret reward for tipping — not advertised, just a surprise */
  const claimTipReward = useCallback(async (userId: string): Promise<{ interest: number; deep: number } | null> => {
    if (!supabase) return null;

    const { data, error } = await supabase.rpc('claim_tip_reward', {
      p_user_id: userId,
      p_interest_bonus: TIP_REWARD_INTEREST,
      p_deep_bonus: TIP_REWARD_DEEP,
    });

    if (error || !data) return null;

    await loadCredits(userId);
    return { interest: TIP_REWARD_INTEREST, deep: TIP_REWARD_DEEP };
  }, [loadCredits]);

  /** Award credits to a referrer when a new user signs up via their link (one-time) */
  const claimReferralReward = useCallback(async (newUserId: string, referrerRef: string): Promise<{ interest: number; deep: number } | null> => {
    if (!supabase) return null;

    // Resolve referral: could be a short code (e.g. "a7k3m9x2") or a legacy UUID
    const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(referrerRef);
    let referrerUserId = referrerRef;

    if (!isUUID) {
      const { data: resolved } = await supabase.rpc('resolve_referral_code', { code: referrerRef });
      if (!resolved) return null;
      referrerUserId = resolved as string;
    }

    // Atomic: set referred_by + award credits to referrer in one transaction
    const { data, error } = await supabase.rpc('claim_referral_reward', {
      p_new_user_id: newUserId,
      p_referrer_id: referrerUserId,
      p_interest_bonus: REFERRAL_REWARD_INTEREST,
      p_deep_bonus: REFERRAL_REWARD_DEEP,
    });

    if (error || !data) return null;

    return { interest: REFERRAL_REWARD_INTEREST, deep: REFERRAL_REWARD_DEEP };
  }, []);

  return {
    credits,
    loading,
    loadCredits,
    clearCredits: useCallback(() => setCredits(null), []),
    ensureUserRow,
    consumeInterestSearch,
    refundInterestSearch,
    consumeDeepSearch,
    claimFirstSaveBonus,
    claimTipReward,
    claimReferralReward,
  };
}
